export type ParsedAmount = { valid: true; value: number | null } | { valid: false };

// "1.234" or "12.345.678": dots that group thousands, the way a Dutch user types them.
const DUTCH_THOUSANDS = /^\d{1,3}(\.\d{3})+$/;
const PLAIN_AMOUNT = /^\d+(\.\d{0,2})?$/;

/**
 * Reads an amount the way a Dutch user types it: "1.234,56" and "1234,5" as well as "1234.56".
 * An empty field is null (the API clears the value). Anything that is not a non-negative amount
 * with at most 2 decimals is invalid, so NaN or a string never reaches the API.
 */
export function parseAmount(input: string | number | null | undefined): ParsedAmount {
    if (input === null || input === undefined) return { valid: true, value: null };

    if (typeof input === "number") {
        return Number.isFinite(input) && input >= 0 ? { valid: true, value: input } : { valid: false };
    }

    let text = input.replace(/[€\s]/g, "");

    if (text === "") return { valid: true, value: null };

    if (text.includes(",")) {
        text = text.replace(/\./g, "").replace(",", ".");
    } else if (DUTCH_THOUSANDS.test(text)) {
        text = text.replace(/\./g, "");
    }

    if (!PLAIN_AMOUNT.test(text)) return { valid: false };

    return { valid: true, value: Number(text) };
}

/** An amount from the API (a number, or a numeric string from a decimal cast) as a number. */
export function toAmount(value: unknown): number | null {
    if (value === null || value === undefined || value === "") return null;

    const amount = typeof value === "number" ? value : Number(value);

    return Number.isFinite(amount) ? amount : null;
}

/** "1.234,50", what an amount field shows; "" for no amount. */
export function formatAmount(value: unknown): string {
    const amount = toAmount(value);

    return amount === null
        ? ""
        : amount.toLocaleString("nl-NL", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
          });
}

/** "€ 1.234,50", what a card shows; "-" for no amount. */
export function formatEuro(value: unknown): string {
    const amount = toAmount(value);

    return amount === null ? "-" : amount.toLocaleString("nl-NL", { style: "currency", currency: "EUR" });
}
